export interface ImageMatch {
  index: number;
  element: string;
  attachment?: ChatAttachment;
  altText?: string;
  hasExistingSrc: boolean;
}

export interface ChatAttachment {
  type: string;
  name?: string;
  secureName: string;
}

/**
 * Extract Content-ID from cid: URL format
 * @param src - The src attribute value (e.g., "cid:image123")
 * @returns The extracted Content-ID or null if not a cid URL
 */
export const extractContentId = (src: string): string | null => {
  if (!src) return null;

  const cidMatch = src.match(/^cid:(.+)$/i);
  if (!cidMatch) return null;
  const cid = cidMatch[1];
  return cid !== undefined ? cid.trim() : null;
};

/**
 * Match an img element to an attachment using multiple strategies
 * Priority: Content-ID > Alt text > Data attributes > Positional
 *
 * @param img - The img element from parsed HTML
 * @param attachments - Array of email attachments
 * @param usedAttachments - Set of already matched attachment secureNames
 * @returns Matched attachment or null
 */
export const matchImageToAttachment = (
  img: Element,
  attachments: ChatAttachment[],
  usedAttachments: Set<string>
): ChatAttachment | null => {
  const src = img.getAttribute("src");
  const alt = img.getAttribute("alt");
  const dataFilename = img.getAttribute("data-filename");

  // Strategy 1: Content-ID matching (cid: references)
  if (src) {
    const cid = extractContentId(src);
    if (cid) {
      const cidLower = cid.toLowerCase();
      const match = attachments.find(
        (att) =>
          !usedAttachments.has(att.secureName) &&
          (att.name?.toLowerCase() === cidLower || att.name?.toLowerCase().includes(cidLower))
      );
      if (match) return match;
    }
  }

  // Strategy 2: Alt text matching (case-insensitive)
  if (alt) {
    const altLower = alt.toLowerCase();
    const match = attachments.find(
      (att) => !usedAttachments.has(att.secureName) && att.name?.toLowerCase() === altLower
    );
    if (match) return match;
  }

  // Strategy 3: Data attribute matching (case-insensitive)
  if (dataFilename) {
    const dataFilenameLower = dataFilename.toLowerCase();
    const match = attachments.find(
      (att) => !usedAttachments.has(att.secureName) && att.name?.toLowerCase() === dataFilenameLower
    );
    if (match) return match;
  }

  // Strategy 4: Positional matching (first unused image attachment)
  // Check by type or by common image file extensions
  const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".svg", ".bmp", ".ico"];
  const imageAttachment = attachments.find((att) => {
    if (usedAttachments.has(att.secureName)) return false;

    // Check if type indicates it's an image
    if (att.type === "image" || att.type?.startsWith("image/")) return true;

    // If type is empty/unknown, check file extension
    if (att.name && (!att.type || att.type.trim() === "")) {
      const nameLower = att.name.toLowerCase();
      return imageExtensions.some((ext) => nameLower.endsWith(ext));
    }

    return false;
  });

  return imageAttachment || null;
};

/**
 * Parse email HTML and match all img elements to attachments
 *
 * @param html - Sanitized HTML content
 * @param attachments - Array of email attachments
 * @returns Array of ImageMatch objects with matching information
 */
export const parseEmailImages = (html: string, attachments?: ChatAttachment[]): ImageMatch[] => {
  if (!html || !attachments || attachments.length === 0) {
    return [];
  }

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const images = doc.querySelectorAll("img");

    if (images.length === 0) {
      return [];
    }

    const matches: ImageMatch[] = [];
    const usedAttachments = new Set<string>();

    images.forEach((img, index) => {
      const src = img.getAttribute("src");
      const alt = img.getAttribute("alt");
      const hasExistingSrc = !!src && !src.startsWith("cid:");

      // Match image to attachment
      const attachment = matchImageToAttachment(img, attachments, usedAttachments);

      // Mark attachment as used to prevent duplicate matching
      if (attachment) {
        usedAttachments.add(attachment.secureName);
      }

      matches.push({
        index,
        element: img.outerHTML,
        attachment: attachment ?? undefined,
        altText: alt || undefined,
        hasExistingSrc,
      });
    });

    return matches;
  } catch (error) {
    console.error("Error parsing email images:", error);
    return [];
  }
};
